import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Save, ExternalLink, Sparkles, ChevronDown, ChevronRight, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Admin → Landings.
 * Each landing is one CMS-editable lead-capture page. Slug = URL path.
 *
 * Everything user-facing is editable here. The trust strip and destinations
 * lists are JSON arrays so admins can add/remove rows without redeploy.
 */
export default function AdminLandings() {
  const [landings, setLandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingSlug, setCreatingSlug] = useState("");
  const [openSlug, setOpenSlug] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/landings");
      setLandings(data || []);
      if ((data || []).length > 0 && !openSlug) setOpenSlug(data[0].slug);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, "Could not load landings."));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    let cancelled = false;
    api.get("/landings")
      .then(({ data }) => {
        if (cancelled) return;
        setLandings(data || []);
        if ((data || []).length > 0) setOpenSlug(data[0].slug);
      })
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail, "Could not load landings.")))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const createLanding = async () => {
    const slug = creatingSlug.trim().toLowerCase().replace(/[^a-z0-9-]/g, "");
    if (!slug) return toast.error("Slug is required (e.g. 'goa').");
    try {
      const { data } = await api.post("/landings", { slug });
      setLandings((all) => [...all, data]);
      setCreatingSlug("");
      setOpenSlug(slug);
      toast.success(`Landing /${slug} created.`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, "Could not create landing."));
    }
  };

  const removeLanding = async (slug) => {
    if (!window.confirm(`Delete landing /${slug}? Ad traffic to that URL will redirect to the home page.`)) return;
    try {
      await api.delete(`/landings/${slug}`);
      setLandings((all) => all.filter((l) => l.slug !== slug));
      toast.success(`Landing /${slug} deleted.`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-landings-page">
      <header className="mb-8">
        <div className="uppercase tracking-[0.3em] text-[10px] text-charcoal/50 mb-2">Ad funnels</div>
        <h1 className="font-display text-3xl md:text-4xl text-charcoal">Landing pages</h1>
        <p className="text-charcoal/65 font-light mt-2 max-w-2xl">
          Each landing is one URL ad couples land on after clicking a Meta or Google ad.
          The slug becomes the URL — <code className="font-mono">/start</code> for the default,
          <code className="font-mono"> /goa</code>, <code className="font-mono">/thailand</code>, etc. for destination-specific funnels.
        </p>
      </header>

      {/* Create */}
      <section className="bg-white border border-beige p-5 md:p-6 mb-8 max-w-2xl" data-testid="landings-create">
        <div className="flex items-center gap-2 text-teal-600 mb-1">
          <Sparkles className="h-4 w-4" strokeWidth={1.5} />
          <span className="uppercase tracking-[0.22em] text-[11px] font-medium">Create a new landing</span>
        </div>
        <p className="text-charcoal/60 text-xs mb-4">
          The slug becomes the URL path. Lowercase, letters/numbers/hyphens only.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex items-center bg-ivory border border-beige flex-1 px-3">
            <span className="text-charcoal/40 text-sm">/</span>
            <input
              value={creatingSlug}
              onChange={(e) => setCreatingSlug(e.target.value.toLowerCase())}
              onKeyDown={(e) => e.key === "Enter" && createLanding()}
              placeholder="goa"
              data-testid="landing-new-slug"
              className="flex-1 h-11 bg-transparent outline-none text-base font-light"
            />
          </div>
          <button type="button" onClick={createLanding} data-testid="landing-new-submit" className="btn-primary h-11 px-5">
            <Plus className="h-4 w-4" /> Create
          </button>
        </div>
      </section>

      {/* List */}
      {loading ? (
        <div className="bg-white border border-beige p-10 text-center text-charcoal/40">Loading&hellip;</div>
      ) : landings.length === 0 ? (
        <div className="bg-white border border-beige p-10 text-center text-charcoal/40 italic">No landing pages yet.</div>
      ) : (
        <div className="space-y-4">
          {landings.map((l) => (
            <LandingCard
              key={l.slug}
              landing={l}
              open={openSlug === l.slug}
              onToggle={() => setOpenSlug(openSlug === l.slug ? null : l.slug)}
              onSaved={(fresh) => setLandings((all) => all.map((x) => (x.slug === fresh.slug ? fresh : x)))}
              onDelete={() => removeLanding(l.slug)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function LandingCard({ landing, open, onToggle, onSaved, onDelete }) {
  const [draft, setDraft] = useState(() => initDraft(landing));
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(initDraft(landing)); }, [landing]);

  const set = (path, value) => setDraft((d) => deepSet(d, path, value));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        is_active: draft.is_active,
        label: draft.label,
        headline: draft.headline,
        subheadline: draft.subheadline,
        trust_strip: safeJsonArray(draft.trust_strip_json, "trust strip"),
        destinations: safeJsonArray(draft.destinations_json, "destinations"),
        form_labels: draft.form_labels,
        background_image_url: draft.background_image_url || null,
        background_video_url: draft.background_video_url || null,
        logo_url: draft.logo_url || null,
        success_title: draft.success_title,
        success_message: draft.success_message,
        redirect_url: draft.redirect_url,
        redirect_delay_ms: Number(draft.redirect_delay_ms) || 2000,
        meta: draft.meta,
        noindex: draft.noindex,
      };
      const { data } = await api.patch(`/landings/${landing.slug}`, payload);
      onSaved(data);
      toast.success(`/${landing.slug} saved`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, "Could not save."));
    } finally { setSaving(false); }
  };

  const publicUrl = `${window.location.origin}/${landing.slug}`;

  return (
    <section className="bg-white border border-beige" data-testid={`landing-card-${landing.slug}`}>
      <header className="px-5 md:px-6 py-4 flex items-center justify-between gap-3 cursor-pointer" onClick={onToggle}>
        <div className="flex items-center gap-3 min-w-0">
          {open ? <ChevronDown className="h-4 w-4 text-charcoal/55" /> : <ChevronRight className="h-4 w-4 text-charcoal/55" />}
          <div className="min-w-0">
            <div className="font-display text-xl text-charcoal truncate">/{landing.slug}</div>
            <div className="text-xs text-charcoal/55 truncate">{landing.headline}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 text-[10px] uppercase tracking-[0.22em] ${landing.is_active ? "bg-emerald-100 text-emerald-700" : "bg-charcoal/10 text-charcoal/55"}`}>
            {landing.is_active ? "Live" : "Paused"}
          </span>
          <a href={publicUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-teal-600 hover:underline" data-testid={`landing-open-${landing.slug}`}>
            View <ExternalLink className="h-3 w-3" />
          </a>
          <button type="button" onClick={(e) => { e.stopPropagation(); navigator.clipboard?.writeText(publicUrl); toast.success("URL copied"); }} className="text-[10px] uppercase tracking-[0.22em] text-charcoal/55 hover:text-teal-600" data-testid={`landing-copy-${landing.slug}`}>
            <Copy className="h-3 w-3" />
          </button>
        </div>
      </header>

      {open ? (
        <div className="px-5 md:px-6 pb-6 pt-1 space-y-7">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <Toggle label="Active (live on this URL)" checked={draft.is_active} onChange={(v) => set(["is_active"], v)} testid={`toggle-active-${landing.slug}`} />
            <Toggle label="Block from search engines (noindex)" checked={draft.noindex} onChange={(v) => set(["noindex"], v)} testid={`toggle-noindex-${landing.slug}`} />
          </div>

          <Section title="Hero">
            <Text label="Small label (eyebrow)" value={draft.label} onChange={(v) => set(["label"], v)} />
            <Text label="Headline" value={draft.headline} onChange={(v) => set(["headline"], v)} />
            <TextArea label="Subheadline" value={draft.subheadline} onChange={(v) => set(["subheadline"], v)} />
          </Section>

          <Section title="Trust strip (JSON array)">
            <p className="text-[11px] text-charcoal/55 -mt-2 mb-2">Three stats shown beneath the headline. Each row needs <code>{`{"value":"…","label":"…"}`}</code>.</p>
            <JsonField value={draft.trust_strip_json} onChange={(v) => set(["trust_strip_json"], v)} rows={8} />
          </Section>

          <Section title="Destinations dropdown (JSON array)">
            <p className="text-[11px] text-charcoal/55 -mt-2 mb-2">Options for the &quot;which city&quot; dropdown.</p>
            <JsonField value={draft.destinations_json} onChange={(v) => set(["destinations_json"], v)} rows={8} />
          </Section>

          <Section title="Form labels">
            <Text label="Destination question" value={draft.form_labels.destination} onChange={(v) => set(["form_labels", "destination"], v)} />
            <Text label="Name field" value={draft.form_labels.name} onChange={(v) => set(["form_labels", "name"], v)} />
            <Text label="Phone field" value={draft.form_labels.phone} onChange={(v) => set(["form_labels", "phone"], v)} />
            <Text label="Date field" value={draft.form_labels.wedding_date} onChange={(v) => set(["form_labels", "wedding_date"], v)} />
            <Text label="Submit button" value={draft.form_labels.button} onChange={(v) => set(["form_labels", "button"], v)} />
          </Section>

          <Section title="Visuals">
            <Text label="Background image URL (optional)" value={draft.background_image_url} onChange={(v) => set(["background_image_url"], v)} />
            <Text label="Background video URL (optional)" value={draft.background_video_url} onChange={(v) => set(["background_video_url"], v)} />
            <Text label="Logo URL (optional — falls back to text logo)" value={draft.logo_url} onChange={(v) => set(["logo_url"], v)} />
            <p className="text-[11px] text-charcoal/45">Tip: upload images via the Gallery section (or any other Cloudinary upload) and paste the URL here.</p>
          </Section>

          <Section title="After-submit experience">
            <Text label="Success title" value={draft.success_title} onChange={(v) => set(["success_title"], v)} />
            <TextArea label="Success message" value={draft.success_message} onChange={(v) => set(["success_message"], v)} />
            <Text label="Redirect URL (where to send visitor after submit)" value={draft.redirect_url} onChange={(v) => set(["redirect_url"], v)} />
            <Text label="Redirect delay (ms)" type="number" value={draft.redirect_delay_ms} onChange={(v) => set(["redirect_delay_ms"], v)} />
          </Section>

          <Section title="SEO (browser tab)">
            <Text label="Meta title" value={draft.meta.title} onChange={(v) => set(["meta", "title"], v)} />
            <TextArea label="Meta description" value={draft.meta.description} onChange={(v) => set(["meta", "description"], v)} />
          </Section>

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button type="button" onClick={save} disabled={saving} data-testid={`landing-save-${landing.slug}`} className="btn-primary h-11 px-6 disabled:opacity-60">
              {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving</> : <><Save className="h-4 w-4" /> Save changes</>}
            </button>
            <button type="button" onClick={onDelete} data-testid={`landing-delete-${landing.slug}`} className="inline-flex items-center gap-2 h-11 px-4 border border-rose-300 text-rose-700 text-[11px] uppercase tracking-[0.22em] hover:bg-rose-700 hover:text-ivory">
              <Trash2 className="h-3.5 w-3.5" /> Delete landing
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

// ---------- helpers ----------
function initDraft(l) {
  return {
    is_active: l.is_active !== false,
    noindex: l.noindex !== false,
    label: l.label || "",
    headline: l.headline || "",
    subheadline: l.subheadline || "",
    trust_strip_json: JSON.stringify(l.trust_strip || [], null, 2),
    destinations_json: JSON.stringify(l.destinations || [], null, 2),
    form_labels: {
      destination: l.form_labels?.destination || "Which city are you planning your wedding?",
      name: l.form_labels?.name || "Full Name",
      phone: l.form_labels?.phone || "Mobile Number",
      wedding_date: l.form_labels?.wedding_date || "When is your celebration?",
      button: l.form_labels?.button || "Get Personalised Consultation",
    },
    background_image_url: l.background_image_url || "",
    background_video_url: l.background_video_url || "",
    logo_url: l.logo_url || "",
    success_title: l.success_title || "Thank you.",
    success_message: l.success_message || "Our team will reach out within 24\u201348 hours.",
    redirect_url: l.redirect_url || "/",
    redirect_delay_ms: l.redirect_delay_ms || 2000,
    meta: { title: l.meta?.title || "", description: l.meta?.description || "" },
  };
}

function deepSet(obj, path, value) {
  const next = Array.isArray(obj) ? [...obj] : { ...obj };
  let cur = next;
  for (let i = 0; i < path.length - 1; i++) {
    const k = path[i];
    cur[k] = Array.isArray(cur[k]) ? [...cur[k]] : { ...(cur[k] || {}) };
    cur = cur[k];
  }
  cur[path[path.length - 1]] = value;
  return next;
}

function safeJsonArray(jsonStr, what) {
  try {
    const parsed = JSON.parse(jsonStr || "[]");
    if (!Array.isArray(parsed)) throw new Error("expected array");
    return parsed;
  } catch (e) {
    throw new Error(`Invalid JSON in ${what}: ${e.message}`);
  }
}

function Section({ title, children }) {
  return (
    <div className="border-t border-beige pt-5">
      <div className="font-display text-lg text-charcoal mb-4">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Text({ label, value, onChange, type = "text" }) {
  return (
    <label className="block">
      <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">{label}</span>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full h-10 px-3 bg-ivory border border-beige focus:border-teal-600 outline-none text-sm font-light"
      />
    </label>
  );
}

function TextArea({ label, value, onChange, rows = 2 }) {
  return (
    <label className="block">
      <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">{label}</span>
      <textarea
        value={value || ""}
        rows={rows}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-3 py-2 bg-ivory border border-beige focus:border-teal-600 outline-none text-sm font-light resize-y"
      />
    </label>
  );
}

function JsonField({ value, onChange, rows = 6 }) {
  return (
    <textarea
      value={value}
      rows={rows}
      onChange={(e) => onChange(e.target.value)}
      spellCheck={false}
      className="w-full bg-ivory border border-beige focus:border-teal-600 outline-none p-3 text-[12px] font-mono leading-relaxed text-charcoal resize-y"
    />
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        data-testid={testid}
        className={`relative inline-flex h-6 w-11 items-center transition-colors ${checked ? "bg-teal-600" : "bg-charcoal/20"}`}
      >
        <span className={`inline-block h-4 w-4 bg-white shadow transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
      </button>
      <span className="text-sm text-charcoal">{label}</span>
    </label>
  );
}
